import React from 'react';

interface BowtieIconProps {
  width?: number;
  height?: number;
  className?: string;
}

const BowtieIcon: React.FC<BowtieIconProps> = ({ 
  width = 200, 
  height = 200, 
  className 
}) => {
  // Generate unique IDs for this instance to avoid conflicts
  const uniqueId = React.useMemo(() => Math.random().toString(36).substr(2, 9), []);
  
  return (
    <svg 
      width={width} 
      height={height} 
      viewBox="0 0 100 100" 
      xmlns="http://www.w3.org/2000/svg"
      className={className}
    >
      <defs>
        <linearGradient id={`bowtieGradient-${uniqueId}`} x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" style={{stopColor: 'var(--bowtie-gradient-start)', stopOpacity: 1}} />
          <stop offset="50%" style={{stopColor: 'var(--bowtie-gradient-middle)', stopOpacity: 1}} />
          <stop offset="100%" style={{stopColor: 'var(--bowtie-gradient-end)', stopOpacity: 1}} />
        </linearGradient>
        
        <pattern id={`subtlePattern-${uniqueId}`} x="0" y="0" width="4" height="4" patternUnits="userSpaceOnUse">
          <circle cx="1" cy="1" r="0.5" fill="#ffffff" opacity="0.05"/>
          <circle cx="3" cy="3" r="0.5" fill="#ffffff" opacity="0.05"/>
        </pattern>
        
        <filter id={`glow-${uniqueId}`}>
          <feGaussianBlur stdDeviation="3" result="coloredBlur"/>
          <feMerge>
            <feMergeNode in="coloredBlur"/>
            <feMergeNode in="SourceGraphic"/>
          </feMerge>
        </filter>
      </defs>
      
      {/* Left wing of bowtie */}
      <path d="M 15 35 
               Q 5 25, 5 50
               Q 5 75, 15 65
               L 35 55
               Q 40 50, 35 45
               Z" 
            fill={`url(#bowtieGradient-${uniqueId})`} 
            stroke="var(--bowtie-stroke-color)" 
            strokeWidth="0.5"/>
      
      {/* Right wing of bowtie */}
      <path d="M 85 35 
               Q 95 25, 95 50
               Q 95 75, 85 65
               L 65 55
               Q 60 50, 65 45
               Z" 
            fill={`url(#bowtieGradient-${uniqueId})`} 
            stroke="var(--bowtie-stroke-color)" 
            strokeWidth="0.5"/>
      
      {/* Center knot with geometric design */}
      <g id={`centerKnot-${uniqueId}`}>
        {/* Main knot shape */}
        <rect x="40" y="42" width="20" height="16" rx="2" fill={`url(#bowtieGradient-${uniqueId})`}/>
        
        {/* Geometric accent lines */}
        <line x1="45" y1="47" x2="55" y2="47" stroke="#ffffff" strokeWidth="1.2" opacity="0.6"/>
        <line x1="45" y1="53" x2="55" y2="53" stroke="#ffffff" strokeWidth="1.2" opacity="0.6"/>
        
        {/* Tech-inspired dots */}
        <circle cx="43" cy="50" r="0.8" fill="#ffffff" opacity="0.6"/>
        <circle cx="57" cy="50" r="0.8" fill="#ffffff" opacity="0.6"/>
      </g>
      
      {/* Highlight accents */}
      <path d="M 25 40 Q 30 38, 35 40" 
            fill="none" 
            stroke="#ffffff" 
            strokeWidth="0.8" 
            opacity="0.4"/>
      <path d="M 65 40 Q 70 38, 75 40" 
            fill="none" 
            stroke="#ffffff" 
            strokeWidth="0.8" 
            opacity="0.4"/>
    </svg>
  );
};

export default BowtieIcon;
